import { useResource } from '@landsbankinn/l16n';
import {
  Amount,
  Checkbox,
  type ColumnDef,
  DataGrid,
  formatAccountId,
  getMainCheckboxState,
  H1,
  HeaderCell,
  RadioInput,
  Span,
  TableCaption,
  useUIKitContext,
  VisuallyHidden,
} from '@landsbankinn/ui-kit';
import { type Account, ChoiceType } from '../../../state/server';
import { res } from './Accounts.res';

export function AccountsTable(props: {
  availableAccounts: Account[];
  selectedAccountIds: string[];
  choiceType: ChoiceType;
  title?: string;
  description?: string;
  onChange?: (value: string) => void;
  onSelectAll?: (ids: string[]) => void;
}) {
  const { r } = useResource();
  const { res: uiKitRes } = useUIKitContext();

  const onChange = (accountId: string) => {
    props.onChange?.(accountId);
  };

  const handleSelectAll = () => {
    if (!props.onSelectAll) return;
    const allIds = props.availableAccounts.map((a) => a.id);
    const allSelected =
      props.selectedAccountIds.length === props.availableAccounts.length;
    props.onSelectAll(allSelected ? [] : allIds);
  };

  const mainCheckboxState =
    props.choiceType === ChoiceType.Multiple
      ? getMainCheckboxState(
          props.availableAccounts.map((a) =>
            props.selectedAccountIds.includes(a.id),
          ),
          new Array(props.availableAccounts.length).fill(false),
        )
      : 'false';

  const selectionColumn: ColumnDef<Account, unknown>[] =
    props.choiceType === ChoiceType.None
      ? []
      : [
          {
            id: 'selection',
            accessorKey: 'id',
            header: (args) => (
              <HeaderCell headerArgs={args}>
                {props.choiceType === ChoiceType.Multiple ? (
                  <Checkbox
                    checked={mainCheckboxState === 'true'}
                    indeterminate={mainCheckboxState === 'mixed'}
                    onChange={handleSelectAll}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={r(uiKitRes.selectAll)}
                    skipValidation
                  />
                ) : (
                  <VisuallyHidden inline>{r(res.selectColumn)}</VisuallyHidden>
                )}
              </HeaderCell>
            ),
            enableSorting: false,
            meta: {
              align: 'left',
              width: 48,
            },
            cell: (info) => {
              const account = info.row.original;
              const isSelected = props.selectedAccountIds.includes(account.id);

              if (props.choiceType === ChoiceType.Single) {
                return (
                  <RadioInput
                    checked={isSelected}
                    onChange={() => onChange(account.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                );
              }

              return (
                <Checkbox
                  checked={isSelected}
                  onChange={() => onChange(account.id)}
                  onClick={(e) => e.stopPropagation()}
                  skipValidation
                />
              );
            },
          },
        ];
  const columns: ColumnDef<Account, unknown>[] = [
    ...selectionColumn,
    {
      header: (args) => (
        <HeaderCell headerArgs={args}>
          <Span fontVariant="body small" fontWeight="bold">
            {r(res.number)}
          </Span>
        </HeaderCell>
      ),
      accessorKey: 'id',
      meta: {
        align: 'left',
        width: 200,
      },
      cell: (info) => {
        const account = info.row.original;
        const label = formatAccountId(account.id) ?? account.id;
        return <Span fontVariant="table">{label}</Span>;
      },
    },
    {
      header: (args) => (
        <HeaderCell headerArgs={args}>
          <Span fontVariant="body small">{r(res.name)}</Span>
        </HeaderCell>
      ),
      accessorKey: 'name',
      meta: {
        align: 'left',
      },
    },
    {
      header: (args) => (
        <HeaderCell headerArgs={args}>
          <Span fontVariant="body small">{r(res.balance)}</Span>
        </HeaderCell>
      ),
      accessorKey: 'balance',
      meta: {
        align: 'right',
      },
      cell: (info) => {
        const account = info.row.original;
        return (
          <Amount
            value={account.balance}
            type="currency"
            currency={account.currency}
          />
        );
      },
    },
    {
      header: (args) => (
        <HeaderCell headerArgs={args}>
          <Span fontVariant="body small">{r(res.availableAmount)}</Span>
        </HeaderCell>
      ),
      accessorKey: 'availableFunds',
      meta: {
        align: 'right',
      },
      cell: (info) => {
        const account = info.row.original;
        return (
          <Amount
            value={account.availableFunds}
            type="currency"
            currency={account.currency}
          />
        );
      },
    },
  ];

  return (
    <>
      <TableCaption
        title={<H1 fontVariant="h4">{props.title ?? r(res.selectAccounts)}</H1>}
        description={props.description ?? r(res.selectAccountsDescription)}
      />
      <DataGrid
        data={props.availableAccounts}
        columns={columns}
        getRowId={(row) => row.id}
      />
    </>
  );
}
